// src/promo/membershipCheckout.js
//
// Single source of truth for membership Stripe price IDs + the
// "create-checkout-session → redirectToCheckout" dance. BOTH the wallet
// (BillingPanel) and the bottom-left promo pill import from here, so the
// price table and checkout logic never drift apart (and a logged-in user
// who taps the pill can go STRAIGHT to Stripe without a stop on the wallet).
//
// Resolution order for each plan/cycle (unchanged from BillingPanel):
//   1. REACT_APP_STRIPE_PRICE_<PLAN>[_YEARLY] env var (per-machine override)
//   2. MEMBERSHIP_DEFAULT_PRICE_IDS[plan][cycle] (production defaults)
//   3. (super only) vipPassPlans from the backend subscription doc
// NOTE: CRA inlines process.env.REACT_APP_* only for STATICALLY-written
// names, so every override is referenced literally below (no dynamic keys).

import { loadStripe } from '@stripe/stripe-js';
import { vipPassPlans } from '../Lexappstore/pricing';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Old prices we must never resurrect even if something still hands them in.
const LEGACY_DISABLED_MEMBERSHIP_PRICE_IDS = new Set([
  'price_1RqreDDiAVRhYl1XGckbmvxX',
  'price_1RqrjwDiAVRhYl1X867Ge2wI',
]);

export function pickMembershipPriceId(...candidates) {
  for (const raw of candidates) {
    const id = String(raw || '').trim();
    if (!id) continue;
    if (LEGACY_DISABLED_MEMBERSHIP_PRICE_IDS.has(id)) continue;
    return id;
  }
  return '';
}

export const MEMBERSHIP_DEFAULT_PRICE_IDS = {
  super: {
    monthly: 'price_1TQ0yCDiAVRhYl1XZhPpg09f',
    yearly: 'price_1TQ0yDDiAVRhYl1XpI7YMb0R',
  },
  creator: {
    monthly: 'price_1TQ0yEDiAVRhYl1XSngE283b',
    yearly: 'price_1TQ0yEDiAVRhYl1XrSDrrpfb',
  },
  pro: {
    monthly: 'price_1TQ0yFDiAVRhYl1Xch3FgLsJ',
    yearly: 'price_1TQ0yGDiAVRhYl1Xtj4Pyanz',
  },
  business: {
    monthly: 'price_1TQ0yHDiAVRhYl1Xw8DSVh5G',
    yearly: 'price_1TQ0yHDiAVRhYl1XDK6cE9yX',
  },
  enterprise: {
    monthly: 'price_1TQ0yIDiAVRhYl1XmvnTSsFL',
    yearly: 'price_1TQ0yJDiAVRhYl1XoWmOKaoT',
  },
};

// Static env-override table (literal references so CRA can inline them).
export const MEMBERSHIP_PRICE_IDS = {
  super: {
    monthly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_SUPER,
      MEMBERSHIP_DEFAULT_PRICE_IDS.super.monthly,
      vipPassPlans?.monthly?.priceId
    ),
    yearly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_SUPER_YEARLY,
      MEMBERSHIP_DEFAULT_PRICE_IDS.super.yearly,
      vipPassPlans?.yearly?.priceId
    ),
  },
  creator: {
    monthly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_CREATOR,
      MEMBERSHIP_DEFAULT_PRICE_IDS.creator.monthly
    ),
    yearly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_CREATOR_YEARLY,
      MEMBERSHIP_DEFAULT_PRICE_IDS.creator.yearly
    ),
  },
  pro: {
    monthly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_PRO,
      MEMBERSHIP_DEFAULT_PRICE_IDS.pro.monthly
    ),
    yearly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_PRO_YEARLY,
      MEMBERSHIP_DEFAULT_PRICE_IDS.pro.yearly
    ),
  },
  business: {
    monthly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_BUSINESS,
      MEMBERSHIP_DEFAULT_PRICE_IDS.business.monthly
    ),
    yearly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_BUSINESS_YEARLY,
      MEMBERSHIP_DEFAULT_PRICE_IDS.business.yearly
    ),
  },
  enterprise: {
    monthly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_ENTERPRISE,
      MEMBERSHIP_DEFAULT_PRICE_IDS.enterprise.monthly
    ),
    yearly: pickMembershipPriceId(
      process.env.REACT_APP_STRIPE_PRICE_ENTERPRISE_YEARLY,
      MEMBERSHIP_DEFAULT_PRICE_IDS.enterprise.yearly
    ),
  },
};

// Resolve the Stripe priceId for a plan/cycle, '' if none configured.
export function resolveMembershipPriceId(planKey, cycle) {
  const key = String(planKey || '').trim().toLowerCase();
  const cyc = cycle === 'yearly' ? 'yearly' : 'monthly';
  return String(MEMBERSHIP_PRICE_IDS[key]?.[cyc] || '').trim();
}

export const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    'pk_live_51LYeEmDiAVRhYl1XDXtlBpiaE3sYBQOMO7YE7o4Xu8oP6BRkxAytfNdc876Cd3m3ohHjLoHcIyQwWY6R2iXNh0jA00UjgZ0dQt'
);

/**
 * Create a Stripe Checkout Session for a NEW membership subscription and
 * redirect the browser to it. Used for the "free → paid" (pay-now) case —
 * existing-member upgrades go through /api/switch-subscription instead.
 *
 * Throws on any failure (no priceId, network, Stripe load, redirect error)
 * so the caller can fall back (e.g. to the wallet autobuy handoff).
 *
 * @returns {Promise<void>} resolves only if the redirect is in flight.
 */
export async function startMembershipCheckout({
  planKey,
  cycle = 'monthly',
  promoId = '',
  returnTo = '',
  successReturnTo = '',
  customerEmail = '',
  idToken,
}) {
  const priceId = resolveMembershipPriceId(planKey, cycle);
  if (!priceId) throw new Error(`No Stripe price configured for ${planKey}/${cycle}`);
  if (!idToken) throw new Error('Authentication required to start checkout');

  const safeReturn =
    typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//')
      ? returnTo
      : '/profile/wallet';

  const resp = await fetch(`${API_BASE}/api/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      priceId,
      mode: 'subscription',
      productType: 'vip_pass',
      returnTo: safeReturn,
      // Optional separate SUCCESS target (cancel still returns to `returnTo`).
      ...(typeof successReturnTo === 'string' &&
      successReturnTo.startsWith('/') &&
      !successReturnTo.startsWith('//')
        ? { successReturnTo }
        : {}),
      // Promo coupon handoff (backend validates + pins to this price).
      ...(promoId ? { promoId } : {}),
      ...(customerEmail ? { customerEmail } : {}),
      // So Stripe's "back" returns to the page they started on.
      returnUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    }),
  });

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || data?.error || !data?.sessionId) {
    throw new Error(data?.error || `Checkout failed (HTTP ${resp.status})`);
  }

  const stripe = await stripePromise;
  if (!stripe) throw new Error('Stripe failed to load');

  const result = await stripe.redirectToCheckout({ sessionId: data.sessionId });
  if (result?.error?.message) throw new Error(result.error.message);
}
